import React, { useEffect, useRef, useState } from "react";
import { FilterValue } from "../../components";
import { secondsToHms } from "../../utils";
import styles from "../../scss/media.module.scss";
import { Tooltip } from "../../components";
import { MediaModel } from "@/database";
import GenerateThumb from "../../assets/icons/svg/generate_thumb.svg";
import FailureThumb from "../../assets/icons/svg/failure.svg";
import Filter from "../../assets/icons/svg/filter_list.svg?react";
import Video from "../../assets/icons/svg/video.svg?react";
import Audio from "../../assets/icons/svg/audio.svg?react";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { THUMBNAIL_SIZE_SUFFIX } from "@/utils";
import { useNavigate } from "react-router-dom";
import { setMediaDetails } from "@/store/media/media.slice";
import { useDispatch } from "react-redux";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";
import noImage from "../../assets/icons/svg/no-image.svg";
import {
  handleFilterChangeUtil,
  Display_Value_Key as dvk,
} from "@/components/Create/utils";
import { ProgressiveImage } from "@/components/ProgressiveImage/ProgressiveImage";
import RenderAction from "./RenderAction";
import { Play, Cloud } from "lucide-react";
import {
  extractImageColors,
  generateGradient,
  generateBackgroundColor,
} from "@/utils/colorExtractor";

export const RenderDescription: React.FC<{
  media: MediaModel;
  onFilterAdd?: (
    key: string,
    value?: FilterValue,
    displayValue?: FilterValue
  ) => void;
  onMediaPreview?: (slikeid: string) => void;
  filters?: Record<string, FilterValue>;
  updateIsFilter: (isFilter: boolean) => void;
  updateIsClearFilter: (isClearFilter: boolean) => void;
  handleSetFilters: (filters: Record<string, FilterValue>) => void;
  useProgressiveLoading?: boolean;
  tab?: string;
  setIsMediaModalOpen?: (isOpen: boolean) => void;
  setIsSocialModalOpen?: (isOpen: boolean) => void;
  setFooterBtnContent?: (content: string) => void;
  setClonedMedia?: (media: MediaModel) => void;
  onCloneInitiated?: () => void;
  isDropdownOpen?: boolean;
  onDropdownStateChange?: (isOpen: boolean) => void;
}> = ({
  media,
  onMediaPreview,
  filters,
  updateIsFilter,
  updateIsClearFilter,
  handleSetFilters,
  useProgressiveLoading = true,
  tab,
  setIsMediaModalOpen,
  setIsSocialModalOpen,
  setFooterBtnContent,
  setClonedMedia,
  onCloneInitiated,
  isDropdownOpen = false,
  onDropdownStateChange,
}) => {
  const imgRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isValidImage, setIsValidImage] = useState(true);
  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>(
    {}
  );

  const isPortraitVideo =
    media?.meta?.height > media?.meta?.width || media?.is_vertical === 1;

  const handleImageError = () => {
    setIsValidImage(false);
  };

  const handleMediaEdit = () => {
    if (media.status !== -2 && media.media_status !== -3) {
      dispatch(setMediaDetails({ data: { media } }));
      const currentParams = new URLSearchParams(window.location.search);
      const cleanParams = new URLSearchParams();
      currentParams.forEach((value, key) => {
        if (value && !value.includes("nextInit, navigateOptions")) {
          cleanParams.append(key, value);
        }
      });
      if (tab) {
        cleanParams.append("tab", tab);
      }
      navigate(`/media/${media.id}/details?${cleanParams.toString()}`);
    }
  };

  const isPremiereMedia =
    media?.media_ref?.rel === "premiere" || media?.asset_type === "premiere";

  const thumbnailImageId = isPremiereMedia
    ? media?.images?.premiere
    : isPortraitVideo && media?.images?.portrait
      ? media.images.portrait
      : media?.images?.thumb || media?.images?.premiere;

  // Extract colors from thumbnail for background
  useEffect(() => {
    const extractColors = async () => {
      if (!isValidImage || !thumbnailImageId) {
        setBackgroundStyle({});
        return;
      }

      try {
        const baseUrl = generateBaseUrl(media);
        if (!baseUrl) {
          setBackgroundStyle({});
          return;
        }

        const imageSrc = getThumbnailUrlUtil({
          media,
          id: thumbnailImageId,
          source: media?.source,
          sizeSuffix: THUMBNAIL_SIZE_SUFFIX,
        });
        const colorPalette = await extractImageColors(imageSrc);
        const backgroundGradient = generateGradient(colorPalette);
        setBackgroundStyle({
          background: backgroundGradient,
          transition: "background 0.3s ease-in-out",
        });
      } catch (error) {
        setBackgroundStyle({});
      }
    };

    extractColors();
  }, [media, isValidImage, isPortraitVideo, thumbnailImageId]);

  const searchParams = new URLSearchParams(window.location.search);

  const currentState = media?.state < 0 ? 12 : media?.state;
  const processingSubStates = [3, 7, 8, 9];
  const broadSteps = [
    { key: "created", label: "Created", states: [1] },
    { key: "uploading", label: "Uploading", states: [2] },
    { key: "processing", label: "Processing", states: processingSubStates },
  ];
  const stepShare = 100 / broadSteps.length;
  const overallPercent =
    currentState === 12
      ? 0
      : Math.round(
          broadSteps.reduce((acc, step) => {
            const maxState = Math.max(...step.states);
            if (currentState > maxState) return acc + stepShare;
            if (step.states.includes(currentState)) {
              const subIndex = step.states.indexOf(currentState);
              const subShare = stepShare / step.states.length;
              return acc + subShare * (subIndex + 1);
            }
            return acc;
          }, 0)
        );

  const circleRadius = 16;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const circleOffset =
    circleCircumference - (overallPercent / 100) * circleCircumference;

  return (
    <div className="d-flex gap-3">
      <div className="position-relative">
        <div
          className={`rounded ratio ratio-16x9 border pointer ${
            media?.state !== 10 ? "opacity-60" : ""
          }`}
          style={{
            width: "124px",
            minWidth: "124px",
            cursor: media?.status !== 5 ? "auto" : "pointer",
            ...backgroundStyle,
          }}
          onMouseEnter={(e) => {
            if (media?.status === 5 && media?.state === 10) {
              const overlay = e.currentTarget.querySelector(
                ".media-hover-overlay"
              ) as HTMLElement;
              if (overlay) overlay.style.opacity = "1";
            }
          }}
          onMouseLeave={(e) => {
            if (media?.status === 5 && media?.state === 10) {
              const overlay = e.currentTarget.querySelector(
                ".media-hover-overlay"
              ) as HTMLElement;
              if (overlay) overlay.style.opacity = "0";
            }
          }}
        >
          {media?.status === -2 ? (
            <div>
              <img
                src={FailureThumb}
                className="w-100"
                alt="Failure Thumbnail"
              />
            </div>
          ) : !generateBaseUrl(media) || !thumbnailImageId ? (
            <div>
              <img src={GenerateThumb} className="w-100" alt="Generate Thumb" />
            </div>
          ) : (
            <div
              onClick={() => {
                media?.state === 10 && onMediaPreview?.(media.id);
              }}
            >
              <ProgressiveImage
                src={
                  isValidImage && thumbnailImageId
                    ? getThumbnailUrlUtil({
                        media,
                        id: thumbnailImageId,
                        source: media?.source,
                        sizeSuffix: THUMBNAIL_SIZE_SUFFIX,
                      })
                    : noImage
                }
                className={`position-absolute top-50 start-50 translate-middle`}
                alt="Media Thumbnail"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: isPortraitVideo ? "contain" : "cover",
                  opacity: media?.status === 5 && media?.state === 10 ? 1 : 0.4,
                  cursor:
                    media?.status === 5 && media?.state === 10
                      ? "pointer"
                      : "default",
                }}
                onError={handleImageError}
                useProgressiveLoading={useProgressiveLoading}
              />
              {media?.state === 10 ? (
                <div className="media-hover-overlay play-button">
                  <Play size={30} />
                </div>
              ) : null}
            </div>
          )}
          {media?.status !== -2 && media?.state === 10 && (
            <span className="timeCard position-absolute end-0 bottom-0">
              {secondsToHms(media?.meta?.duration ?? 0)}
            </span>
          )}
          {media?.state === 10 && (
            <span
              className="timeCard position-absolute start-0 top-0 H"
              style={{ fontSize: "9px" }}
            >
              {media?.asset_type === "video" ? (
                <Video />
              ) : media.asset_type === "audio" ? (
                <Audio />
              ) : null}
            </span>
          )}
          {media?.status !== -2 && media?.state === 10 && media?.media_ref?.rel && (
            <span className="timeCard text-uppercase position-absolute top-0 start-0">
              {media?.media_ref?.rel}
            </span>
          )}

          {/* Media Circular Progress Bar */}
          {media?.state !== 10 &&
            media?.state !== -2 &&
            media?.media_status !== -3 && (
              <div
                className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                style={{ width: "42px", height: "42px" }}>
                <svg width="42" height="42" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r={circleRadius}
                    fill="rgba(0,0,0,0.5)"
                    stroke="#999"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r={circleRadius}
                    fill="none"
                    stroke="#0d6efd"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={circleCircumference}
                    strokeDashoffset={circleOffset}
                    transform="rotate(-90 18 18)"
                    style={{ transition: "stroke-dashoffset 0.3s ease" }}
                  />
                </svg>
                <span
                  className="position-absolute text-white"
                  style={{ fontSize: "10px", fontWeight: 500 }}>
                  {overallPercent}%
                </span>
              </div>
            )}
        </div>
      </div>
      <div className="d-flex flex-column flex-grow-1 position-relative">
        <div className="d-flex gap-1">
          <div
            className="anchor clamp-lines clamp-2"
            onClick={handleMediaEdit}
            style={{
              cursor:
                media?.status === -2 || media?.media_status === -3
                  ? "auto"
                  : "pointer",
            }}
          >
            {media.title}
          </div>
        </div>
        <div className="d-flex gap-1 justify-content-start align-items-center">
          {/* <!--- Media Source ---> */}
          <span
            className="H"
            style={{
              background: "rgba(0,0,0,0.3)",
              height: "80%",
              width: "1px",
              margin: "0 10px 0 16px",
            }}
          ></span>
          <div className={`d-flex flex-column H ${styles["hover-filter"]}`}>
            <span className=" text-muted text-capitalize d-flex align-items-center gap-1">
              <span
                className="pointer"
                onClick={() => {
                  trackEvent("browse", {
                    category: "content",
                    entity: "Inline Filter",
                    action: "click",
                    location:
                      typeof window !== "undefined" &&
                      window.location.pathname.startsWith("/discover")
                        ? "discover"
                        : "content",
                    ...getTrackUserContext(),
                  });
                  // onFilterAdd('source', media.source, media.source);
                  const updatefilters = handleFilterChangeUtil(
                    "source",
                    media.source,
                    media.source,
                    filters,
                    updateIsFilter,
                    updateIsClearFilter,
                    dvk
                  );
                  handleSetFilters(updatefilters);
                }}
              >
                <Tooltip
                  content={<p className="p-0 mb-0">Filter by this Source</p>}
                >
                  <Filter className={`${styles["hover-icon"]} iconSvg`} />
                </Tooltip>
              </span>
            </span>
          </div>
        </div>
        {/* Description (default) */}
        <div
          className={`${styles.descToggle} text-muted clamp-lines clamp-2 pt-2 f-11`}
        >
          {media.desc}
        </div>

        {/* Actions (on row hover) */}
        <div
          className={`${styles.actionToggle} position-absolute bottom-0 start-0 pt-1 w-100`}
        >
          <RenderAction
            media={media}
            paramFilter={searchParams}
            tab={tab}
            setIsMediaModalOpen={setIsMediaModalOpen || (() => {})}
            setIsSocialModalOpen={setIsSocialModalOpen || (() => {})}
            setFooterBtnContent={setFooterBtnContent || (() => {})}
            setClonedMedia={setClonedMedia || (() => {})}
            onCloneInitiated={onCloneInitiated}
            onDropdownStateChange={onDropdownStateChange}
          />
        </div>

        {media?.state !== -2 && (
          <div className="actionCont H">
            <button
              className="btn btn-outline-primary"
              onClick={handleMediaEdit}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
